/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { When } from "react-if";
import { ModalConfirm, ExtendedPaginationBox, Text } from "@nordcloud/gnui";
import { KpiTargetSelection } from "~/generated/graphql";
import {
  UniversalWrap,
  ReactTableResizeable,
  ListLoader,
  DataDisplayWrapper,
} from "~/components";
import { KpiTarget } from "~/components/KPI/types";
import { KpiType } from "~/constants";
import { generateActionConfirmText } from "~/tools";
import { PAGE_LIMIT, useKpiTargets } from "./hooks";
import { KpiTargetsListColumns } from "./KpiTargetsListColumns";

type Props = {
  businessContextId: string;
  targetSelection: KpiTargetSelection;
  kpiType: KpiType;
  onArchiveTarget: (id: string, selection: KpiTargetSelection) => void;
  onEditTarget: (kpi: KpiTarget) => void;
};

export function KpiTargetsList({
  businessContextId,
  targetSelection,
  kpiType,
  onArchiveTarget,
  onEditTarget,
}: Props) {
  const [isModalConfirmOpen, setIsModalConfirmOpen] = React.useState(false);
  const [selectedTarget, setSelectedTarget] = React.useState<{
    id: string;
    name: string;
  }>({ id: "", name: "" });

  const { targets, targetsCount, loading, error, page, set } = useKpiTargets(
    businessContextId,
    targetSelection,
    kpiType
  );

  const handleArchiveActionClick = ({ id, name }: KpiTarget) => {
    setSelectedTarget({ id, name });
    setIsModalConfirmOpen(true);
  };

  const handleArchiveModalConfirm = () => {
    onArchiveTarget(selectedTarget.id, targetSelection);
    setIsModalConfirmOpen(false);
  };

  const columns = React.useMemo(
    () =>
      KpiTargetsListColumns({
        onArchiveActionClick:
          targetSelection !== KpiTargetSelection.Archived
            ? handleArchiveActionClick
            : undefined,
        onEditActionClick:
          targetSelection === KpiTargetSelection.Upcoming
            ? onEditTarget
            : undefined,
      }),
    [onEditTarget, targetSelection]
  );

  return (
    <>
      <UniversalWrap
        loaderProps={{
          loading,
          Component: ListLoader,
        }}
        errorProps={{
          error,
        }}
      >
        <DataDisplayWrapper
          messageComponent={<Text>No data available.</Text>}
          hasData={targetsCount > 0}
        >
          <ReactTableResizeable striped columns={columns} data={targets} />
          <When condition={targetsCount > PAGE_LIMIT}>
            <ExtendedPaginationBox
              sidebar
              small
              count={targetsCount}
              from={page * PAGE_LIMIT}
              setSize={() => undefined}
              size={PAGE_LIMIT}
              setPage={(newPage) =>
                set("page")(Math.ceil(newPage / PAGE_LIMIT))
              }
            />
          </When>
        </DataDisplayWrapper>
      </UniversalWrap>

      <ModalConfirm
        isOpen={isModalConfirmOpen}
        confirm={() => handleArchiveModalConfirm()}
        actionLabel="Archive"
        contentLabel="Archive Target"
        onClose={() => setIsModalConfirmOpen(false)}
      >
        {generateActionConfirmText(selectedTarget.name)("archive")()}
      </ModalConfirm>
    </>
  );
}
